import * as yup from "yup"
import * as yupLocale from "../../i18n/yup"
import Database from "crane-query-builder/src/Database"
import I18n from "i18n-js"
import Model from "../../database/Model"
import moment from "moment"
import numeral from "numeral"
import SQLite from 'react-native-sqlite-storage';
import { FarmStoreModel } from "./farm-store/farm-store"
import { Instance, SnapshotOut, types } from "mobx-state-tree"
import { Platform } from "react-native"
import { Toast } from "native-base"
import {
  canBePushedCount,
  Currency,
  Disease,
  Farm,
  FarmParameter,
  FarmUser,
  Hatchery,
  Laboratory,
  Parameter,
  Product,
  Region,
  Species,
  Stock,
  User,
} from "../../database"
import { delay } from "../../utils/delay"
import { withRootStore } from "../extensions/with-root-store"
import { withEnvironment } from "../extensions/with-environment"
import { withBaseStoreModels } from "../extensions/with-base-store-models"
import { IAnyStateTreeNode } from "mobx-state-tree/dist/internal"
import axios from "axios"
import config from "../../utils/config"

const AppStoreProps = {
  appVersion: types.optional(types.string, "1.4.5-20231102"),
  databaseVersion: types.optional(types.string, "20231123140000"),
  mobxVersion: types.optional(types.string, "20230602140000"),
  lastReleaseMessage: types.maybeNull(types.string),
  locale: types.maybeNull(types.optional(types.string, I18n.locale || 'id')),

  farmId: types.maybeNull(types.number),
  farmStoreModels: types.optional(types.array(FarmStoreModel), []),
  diseaseId: types.maybeNull(types.number),
  diseases: types.optional(types.array(types.frozen()), []),

  offlineData: types.maybeNull(types.number),
  progressOffline: types.maybeNull(types.string),
  errorOffline: types.optional(types.frozen(), []),

  queryingFarm: types.maybeNull(types.boolean),
  syncingFarm: types.maybeNull(types.boolean),
  syncedFarm: types.maybeNull(types.boolean),
  queryingDisease: types.maybeNull(types.boolean),
  syncingDisease: types.maybeNull(types.boolean),
  syncedDisease: types.maybeNull(types.boolean),

  isOffline: types.maybeNull(types.boolean)
}

/**
 * Model description here for TypeScript hints.
 */
export const AppStoreModel = types
  .model("AppStore")
  .props(AppStoreProps)
  .extend(withRootStore)
  .extend(withEnvironment)
  .extend(withBaseStoreModels)
  .views((self) => ({
    get farmStore() {
      return self.farmStoreModels.find((farmStore) => farmStore.farmId === self.farmId)
    },
    get farms() {
      return self.farmStoreModels.map((farmStore) => farmStore.farm)
    },
    get loadingFarm() {
      return self.queryingFarm || self.syncingFarm
    }
  })) // eslint-disable-line @typescript-eslint/no-unused-vars
  .actions((self) => ({
    set(key: keyof typeof AppStoreProps, value: any) {
      if (self[key] === value) return
      if (key === 'databaseVersion') this.setDatabaseVersion(value)
      else if (key === 'mobxVersion') this.setMobxVersion(value)
      else if (key === 'locale') this.setLocale(value)
      else self[key] = value as never
    },
    setArrayModelStore(
      key: "farmStoreModels",
      values: Array<any>,
      modelId: string, model: string, modelKey: string,
      defaultStore: (props?: any) => IAnyStateTreeNode
    ) {
      return self.setArrayBaseModelStore(
        key, values, modelId, model, modelKey, defaultStore
      )
    },
    setDatabaseVersion(value) {
      if (self.databaseVersion !== value) {
        const { initialSyncStore } = self.rootStore
        initialSyncStore.setSynced(false)
        canBePushedCount().catch(() => 0).then((count) => {
          if (count) return;
          this.removeDatabase().catch();
        })
      }
      self.databaseVersion = value
    },
    setMobxVersion(value) {
      if (self.mobxVersion !== value) {
        self.rootStore.reset();
      }
      self.mobxVersion = value
    },
    setLocale(value) {
      I18n.locale = value
      moment.locale(value);
      numeral.locale(value);
      if (yupLocale[value])
        yup.setLocale(yupLocale[value]);
      self.locale = value;

    },
    async getFarms(sync = true, onFinishSync = undefined) {
      return self.getModels(
        Farm,
        'queryingFarm', 'syncingFarm', 'syncedFarm',
        { sort: 'name' },
        (models) => {
          this.setArrayModelStore(
            "farmStoreModels",
            models.map((model) => model.toArray()),
            'farmId', 'farm', 'id',
            (props = {}) => FarmStoreModel.create(props)
          )
          if (!models.find((model) => model.getKey() === self.farmId)) {
            this.set('farmId', models[0] && models[0].getKey() || null)
          }
        },
        sync, async () => this.syncFarms(), onFinishSync
      )
    },
    async syncFarms() {
      if (Platform.OS === 'web') return true;

      this.set('syncingFarm', true)
      const success = await Region.syncAll() && await Farm.syncAll(
        {},
        [], true, undefined, (e) => {
          if (e.message && !Toast.isActive(e.name)) Toast.show({ id: e.name, title: e.message });
        }
      )

      this.set('syncingFarm', false)
      return success
    },
    async syncFarmsWithOthers() {
      if (Platform.OS === 'web') return true;

      // this.set('syncingFarm', true)
      const success =
        (await Region.syncAll()) &&
        (await Farm.syncAll({}, [], true, undefined, (e) => {
          if (e.message && !Toast.isActive(e.name)) Toast.show({ id: e.name, title: e.message });
        })) &&
        (await Promise.all([
          Parameter.syncAll(),
          Hatchery.syncAll(),
          Laboratory.syncAll(),
          Product.syncAll(),
          User.syncAll(),
          Currency.syncAll(),
          Species.syncAll()
        ])).reduce((previousValue, currentValue) => previousValue && currentValue, true)

      if (success) {
        const farms = await Farm.fetch().catch((e) => {
          console.warn(e);
          return [];
        })
        if (farms.length) {
          await User.syncAll();
          const farmIds = farms.map((farm: Farm) => farm.getKey())
          await FarmUser.syncAll(
            { farm_id__in: farmIds.join(',') },
            [['farm_user.farm_id', farmIds]]
          ).catch();
          FarmParameter.syncAll(
            { farm_id__in: farmIds.join(',') },
            [['farm_parameter.farm_id', farmIds]]
          ).catch();
          Stock.syncAll(
            { farm_id__in: farmIds.join(',') },
            [['stocks.farm_id', farmIds]]
          ).catch();
        }
      }
      // this.set('syncingFarm', false)
      return success
    },
    async getDiseases(sync = true, onFinishSync = undefined) {
      return self.getModels(
        Disease,
        'queryingDisease', 'syncingDisease', 'syncedDisease',
        { sort: 'full_name,desc' },
        (models) => {
          this.set("diseases", models.map((model) => model.toArray()))
        },
        sync, async () => this.syncDiseases(), onFinishSync
      )
    },
    async syncDiseases() {
      if (Platform.OS === 'web') return true;

      this.set('syncingDisease', true)
      const success = await Disease.syncAll(
        {}, [], false, undefined, (e) => {
          if (e.message && !Toast.isActive(e.name)) Toast.show({ id: e.name, title: e.message });
        }
      )

      this.set('syncingDisease', false)
      return success
    },
    init() {
      const currentAppStore = AppStoreModel.create({})
      if (self.appVersion !== currentAppStore.appVersion)
        this.set('appVersion', currentAppStore.appVersion)
      if (self.databaseVersion !== currentAppStore.databaseVersion)
        this.set('databaseVersion', currentAppStore.databaseVersion)
      if (self.mobxVersion !== currentAppStore.mobxVersion)
        this.set('mobxVersion', currentAppStore.mobxVersion)
    },
    removeDatabase() {
      return new Promise((resolve) => {
        if (Platform.OS === 'web') return resolve(true)
        delay(500).then(() => {
          Database.database.close(async () => {
            await delay(500)
            SQLite.deleteDatabase({
              name: 'Jala.db',
              location: 'default',
              createFromLocation: '~www/Jala.db'
            }, async () => {
              console.log('DB Removed');
              Database.database = null;
              Model.unsetConnectionResolver();
              Model.resolveConnection();
              const { initialSyncStore } = self.rootStore
              initialSyncStore.setSynced(false);

              await delay(500)
              resolve(true);
            })
          })
        })
      })
    },
    isAllowToShowOfflineData() {
      // Farm,
      // FarmParameter,
      // Pond,
      // AutoFeederPond,
      // Cycle,
      // Measurement,
      // Treatment,
      // Feed,
      // Fasting,
      // Sampling,
      // Harvest,
      // Mortality,
      // CycleDisease,
      // File,
      // Product,
      // Stock,
      // FeedingTrayControl
      const {
        waterQualityMultiMeasurementStore,
        chemicalMultiMeasurementStore,
        microbiologyMultiMeasurementStore,
        planktonMultiMeasurementStore,
        treatmentStore,
        feedStore,
        fastingStore,
        samplingStore,
        harvestStore,
        mortalityStore,
        cycleDiseaseStore,
        feedingTrayControlStore,
      } = self.rootStore

      return [
        waterQualityMultiMeasurementStore.modifying,
        chemicalMultiMeasurementStore.modifying,
        microbiologyMultiMeasurementStore.modifying,
        planktonMultiMeasurementStore.modifying,
        treatmentStore.modifying,
        feedStore.modifying,
        fastingStore.modifying,
        samplingStore.modifying,
        harvestStore.modifying,
        mortalityStore.modifying,
        cycleDiseaseStore.modifying,
        feedingTrayControlStore.modifying,
      ].reduce((previousValue, currentValue) => previousValue && !currentValue, true)
    },
    async checkNetworkConnection() {
      const { BASE_URL } = config();
      return axios.get('/ping', {
        baseURL: `${BASE_URL}/api`,
        params: { size: 512 }, // 512kb,
        timeout: 5000,
        cancelToken: new axios.CancelToken(function executor(cancel) {
          setTimeout(() => cancel('Request Timeout'), 5000);
        })
      })
      .then(() => this.set('isOffline', false))
      .catch(() => this.set('isOffline', true))
    }
  })) // eslint-disable-line @typescript-eslint/no-unused-vars

type AppStoreType = Instance<typeof AppStoreModel>
export interface AppStore extends AppStoreType {}
type AppStoreSnapshotType = SnapshotOut<typeof AppStoreModel>
export interface AppStoreSnapshot extends AppStoreSnapshotType {}
export const createAppStoreDefaultModel = () => types.optional(AppStoreModel, {})
